import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  FileText, 
  Users, 
  Settings, 
  FlaskConical,
  ClipboardCheck,
  UserCog,
  FolderKanban,
  Bell,
  FolderOpen,
  Database
} from 'lucide-react';

const NavItem = ({ to, icon: Icon, children }) => {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `sidebar-link ${isActive ? 'active' : ''}`
      }
    >
      <Icon size={18} />
      <span>{children}</span>
    </NavLink>
  );
};

const NavSection = ({ title, children }) => (
  <div className="mb-6">
    <div className="px-4 mb-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
      {title}
    </div>
    <div className="space-y-1">
      {children}
    </div>
  </div>
);

export const Sidebar = () => {
  const { user, isCompanyRep, isCompanyOwner, isAdmin, isCRD, isChairman, isCoordinator, isConsultant, isDirector, isExecDirector, isInternal } = useAuth();

  return (
    <aside className="sidebar" data-testid="sidebar">
      <div className="sidebar-logo">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="ClinicalFlow" className="w-10 h-10 rounded-lg object-contain" />
          <div>
            <h1 className="text-lg font-bold text-white font-['Manrope']">ClinicalFlow</h1>
            <p className="text-xs text-slate-400">Research Portal</p>
          </div>
        </div>
      </div>

      <nav className="sidebar-nav px-3">
        {/* Company Portal */}
        {isCompanyRep() && (
          <NavSection title="Company Portal">
            <NavItem to="/dashboard" icon={LayoutDashboard}>Dashboard</NavItem>
            <NavItem to="/studies" icon={FolderOpen}>Studies</NavItem>
            <NavItem to="/requests" icon={FileText}>My Requests</NavItem>
            <NavItem to="/notifications" icon={Bell}>Notifications</NavItem>
            {isCompanyOwner() && (
              <NavItem to="/company/reps" icon={Users}>Manage Reps</NavItem>
            )}
          </NavSection>
        )}

        {/* Internal Portal */}
        {isInternal() && !isCompanyRep() && (
          <>
            <NavSection title="Dashboard">
              <NavItem to="/dashboard" icon={LayoutDashboard}>Overview</NavItem>
              <NavItem to="/notifications" icon={Bell}>Notifications</NavItem>
            </NavSection>

            <NavSection title="Research">
              <NavItem to="/studies" icon={FolderOpen}>Studies</NavItem>
              <NavItem to="/requests" icon={FileText}>Requests</NavItem>
            </NavSection>
          </>
        )}

        {/* Chairman — pending registration approvals (admin-lite) */}
        {isChairman() && !isAdmin() && (
          <NavSection title="Approvals">
            <NavItem to="/admin/users" icon={ClipboardCheck}>Pending Registrations</NavItem>
          </NavSection>
        )}

        {/* Admin */}
        {isAdmin() && (
          <NavSection title="Administration">
            <NavItem to="/admin/users" icon={Users}>Users</NavItem>
            <NavItem to="/admin/specialties" icon={FlaskConical}>Specialties</NavItem>
            <NavItem to="/admin/config" icon={Settings}>Configuration</NavItem>
            <NavItem to="/admin/backup" icon={Database}>Backup &amp; Restore</NavItem>
          </NavSection>
        )}
      </nav>

      <div className="sidebar-footer">
        <NavLink to="/profile" className="flex items-center gap-3 hover:bg-slate-700/50 rounded-lg p-1 -m-1 transition-colors">
          <div className="w-9 h-9 rounded-full bg-slate-700 flex items-center justify-center">
            <UserCog size={16} className="text-slate-300" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.role}</p>
          </div>
        </NavLink>
      </div>
    </aside>
  );
};
